import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Send, Plus } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../context/RealtimeContext";

const fmt = (s) => new Date(s).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

const NewTicket = ({ onCreated }) => {
  const [subject, setSubject] = useState(() => new URLSearchParams(window.location.search).get("objet") || "");
  const [message, setMessage] = useState("");
  const send = async () => {
    try { const { data } = await http.post("/support/tickets", { subject, message }); setSubject(""); setMessage(""); toast.success("Demande envoyée au service client"); onCreated(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="p-5 space-y-3" data-testid="support-new-form">
      <h2 className="font-display font-bold text-xl">Nouvelle demande</h2>
      <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Objet (ex. problème d'export de factures)" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="support-subject-input" />
      <textarea rows={6} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Décrivez votre problème…" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="support-message-input" />
      <button onClick={send} disabled={subject.trim().length < 3 || !message.trim()} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold disabled:opacity-40" data-testid="support-send-btn"><Send className="h-4 w-4" />Envoyer</button>
    </div>
  );
};

const Thread = ({ t, sup, onChange }) => {
  const [text, setText] = useState("");
  const reply = async () => { try { const { data } = await http.post(`/support/tickets/${t.id}/messages`, { text }); setText(""); onChange(data); } catch (e) { toast.error(formatApiError(e)); } };
  const status = async () => { try { const { data } = await http.post(`/support/tickets/${t.id}/status`, { status: t.status === "open" ? "closed" : "open" }); onChange(data); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="flex flex-col h-full" data-testid="support-thread">
      <div className="p-5 border-b border-slate-200 flex items-center gap-3">
        <div className="min-w-0"><h2 className="font-display font-bold text-xl truncate">{t.subject}</h2><p className="text-xs text-slate-500">{sup && <b>{t.company_name} · </b>}{t.created_by?.name} · {fmt(t.created_at)}</p></div>
        <button onClick={status} className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50" data-testid="support-status-btn">{t.status === "open" ? "Marquer résolue" : "Rouvrir"}</button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-3">
        {t.messages.map((m) => (
          <div key={m.id} className={`max-w-[80%] rounded-lg px-4 py-3 text-sm ${m.side === "support" ? "bg-slate-900 text-white" : "bg-sky-50 border border-sky-100"} ${(m.side === "support") === sup ? "ml-auto" : ""}`} data-testid={`support-msg-${m.id}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-1">{m.author} · {fmt(m.at)}</div>
            <p className="whitespace-pre-wrap">{m.text}</p>
          </div>
        ))}
      </div>
      <div className="p-4 border-t border-slate-200 flex gap-2">
        <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Votre réponse…" className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="support-reply-input" />
        <button onClick={reply} disabled={!text.trim()} className="px-4 rounded-md bg-slate-900 text-white disabled:opacity-40" data-testid="support-reply-btn" aria-label="Envoyer"><Send className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export default function Support() {
  const { user } = useAuth();
  const { lastEvent } = useRealtime();
  const sup = !!(user?.is_superadmin || user?.is_staff);
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [cur, setCur] = useState(null);
  const load = useCallback(() => http.get("/support/tickets").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e))), []);
  const open = useCallback((id) => { setSel(id); http.get(`/support/tickets/${id}`).then((r) => { setCur(r.data); load(); }).catch(() => {}); }, [load]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (lastEvent?.event === "support_changed") { load(); if (sel && lastEvent.payload?.id === sel) open(sel); } }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps
  const unread = (t) => (sup ? t.unread_support : t.unread_client) > 0;

  return (
    <div className="p-4 md:p-8 max-w-[1800px] mx-auto" data-testid="support-page">
      <h1 className="font-display text-4xl font-extrabold tracking-tight flex items-center gap-3"><LifeBuoy className="h-8 w-8" />Service client</h1>
      <p className="text-sm text-slate-600 mb-6">{sup ? "Demandes des entreprises clientes XALTIX" : "Une question, un problème ? L'équipe XALTIX vous répond ici."}</p>
      <div className="grid md:grid-cols-[320px_1fr] border border-slate-200 bg-white rounded-md min-h-[560px] overflow-hidden">
        <div className="border-r border-slate-200 overflow-y-auto">
          {!sup && <button onClick={() => { setSel(null); setCur(null); }} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-sky-700 border-b border-slate-200 hover:bg-sky-50" data-testid="support-new-btn"><Plus className="h-4 w-4" />Nouvelle demande</button>}
          {rows.length === 0 && <p className="p-6 text-sm text-slate-500 text-center">Aucune demande.</p>}
          {rows.map((t) => (
            <button key={t.id} onClick={() => open(t.id)} data-testid={`support-ticket-${t.id}`} className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${sel === t.id ? "bg-slate-100" : ""}`}>
              <div className="flex items-center gap-2"><span className={`text-sm truncate ${unread(t) ? "font-bold" : "font-medium"}`}>{t.subject}</span>{unread(t) && <span className="ml-auto h-2 w-2 rounded-full bg-rose-500 shrink-0" />}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{sup && `${t.company_name} · `}{t.status === "open" ? "En cours" : "Résolue"} · {fmt(t.updated_at)}</div>
            </button>
          ))}
        </div>
        <div className="min-h-[560px]">
          {cur ? <Thread t={cur} sup={sup} onChange={(d) => { setCur(d); load(); }} /> : sup ? <p className="p-10 text-sm text-slate-500">Sélectionnez une demande.</p> : <NewTicket onCreated={(t) => { load(); open(t.id); }} />}
        </div>
      </div>
    </div>
  );
}
